import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import DataTable from "react-data-table-component";
import moment from "moment";

import ExpandedTransaction from "../../components/ExpandedTransaction";

import useTransactions from "../../queries/transaction/getActiveTransactions";
import editTransactionStatus from "../../mutation/transaction/editTransactionNoHook";
import Loader from "../../components/Loader";
import { AuthContext } from "../../utils/userProvider";
import Swal from "sweetalert2";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
} from "../../components/ListPage";

const Transactions = () => {
  const [transactionTable, setTransactionTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);
  const [filterText, setFilterText] = useState("");
  const [selectedYear, setSelectedYear] = useState("last12months");
  const [availableYears, setAvailableYears] = useState([]);

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  let columns = [
    {
      name: "Guest ID",
      selector: "guestId",
      sortable: true,
    },
    {
      name: "Reservation ID",
      selector: "reservationId",
      sortable: true,
    },
    {
      name: "Status",
      selector: "status",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.checkedIn == true && row.status != "cancelled") {
          return <span>Checked-in</span>;
        } else if (row.status == "cancelled" && row.checkedIn == true) {
          return <span>Cancelled (Checked-in)</span>;
        } else {
          return <span>{capitalizeFirstLetter(row.status)}</span>;
        }
      },
    },
    {
      name: "Created At",
      selector: "createdAt",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>{moment(row.createdAt).format("DD MMM YYYY hh:mm:ss A")}</span>
        );
      },
    },
    {
      name: "Payment Method",
      selector: "paymentMethod",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.paymentMethod) {
          if (row.paymentMethod == "bank_transfer") {
            return <span>Bank Transfer</span>;
          } else {
            return <span>{capitalizeFirstLetter(row.paymentMethod)}</span>;
          }
        }
      },
    },
    {
      name: "Stripe ID",
      selector: "stripeId",
      sortable: true,
    },
    {
      name: "Receipt URL",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.stripeUrl) {
          return (
            <a target="_blank" href={row.stripeUrl}>
              Link
            </a>
          );
        } else {
          return null;
        }
      },
    },
    authState.role == "Executive" && {
      name: "Change Status",
      sortable: true,
      cell: (row, index, column, id) => {
        let status = row.status;
        if (row.status == "pencil") {
          return null;
        }
        if (row.status == "confirmed" && row.checkedIn == true) {
          status = "checkedIn";
        }
        let reservationId = row.id;
        return (
          <Select
            value={status}
            onChange={(event, row) => {
              handleStatusChange(event, row, reservationId);
            }}
            defaultValue={status}
          >
            <MenuItem value={"unconfirmed"}>Unconfirmed</MenuItem>
            <MenuItem value={"departed"}>Departed</MenuItem>
            <MenuItem value={"confirmed"}>Confirmed</MenuItem>
            <MenuItem value={"checkedIn"}>Checked-In</MenuItem>
            <MenuItem value={"cancelled"}>Cancelled</MenuItem>
          </Select>
        );
      },
    },
  ];

  const handleStatusChange = (event, row, id) => {
    console.log(id);
    let updateObject = {
      id: id,
      status: event.target.value,
    };
    Swal.fire({
      title: "Updating Status",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: false,
      showConfirmButton: false,
      willOpen: async () => {
        Swal.showLoading();
        await editTransactionStatus(updateObject, authState.jwt)
          .then((response) => {
            const Toast = Swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
              },
            });
            Toast.fire({
              icon: "success",
              title: "Update Successful",
            });
            updateTableData(response.data);
          })
          .catch((err) => {
            let message;
            if (err.errorMessage) {
              if (err.errorMessage.data) {
                message = err.errorMessage.data.message;
              }
            }
            Swal.fire({
              icon: "error",
              titleText: "Error",
              text: message
                ? message
                : "An Error has occured. Please try again.",
              allowOutsideClick: true,
              allowEscapeKey: true,
            });
            updateTableData();
          });
      },
    });
  };

  const updateTableData = (data) => {
    let newTableData = [...transactionTable];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setTransactionTable(newTableData);
    } else {
      setTransactionTable(newTableData);
    }
  };

  // Get transactions for the selected year
  const [response, reset] = useTransactions(selectedYear);

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  // Generate available years (last 5 years and current year)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
    setAvailableYears(years);
  }, []);

  const filteredItems = useMemo(() => {
    if (!response.data) return [];

    return response.data.filter((item) => {
      const matchesSearch = filterText
        ? (item.guestId &&
            item.guestId.toLowerCase().includes(filterText.toLowerCase())) ||
          (item.reservationId &&
            item.reservationId.toLowerCase().includes(filterText.toLowerCase()))
        : true;

      return matchesSearch;
    });
  }, [response.data, filterText]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <div style={{ display: "flex", gap: "10px" }}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 120 }}
          >
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label="Year"
            >
              <MenuItem value="last12months">Last 12 Months</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="filter-text"
            onChange={handleFilterText}
            label="Search"
            variant="outlined"
            size="small"
            value={filterText}
          />
        </div>
      </div>
    );
  }, [filterText, availableYears, selectedYear]);

  return (
    <ListPageShell>
      <ListPageCard>
        {response.loading && <Loader />}
        {!response.loading && (
          <div>
            <ListPageHeader title="Transactions" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              expandableRows
              highlightOnHover
              expandOnRowClicked={true}
              expandableRowsComponent={<ExpandedTransaction />}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
    </ListPageShell>
  );
};

export default Transactions;
